/**
 * Tenant fetch interceptor
 *
 * Wraps window.fetch so every request bound for the backend API carries the
 * x-organization-id header resolved from the site hostname. The backend's
 * tenantContext middleware treats this header as the authoritative tenant
 * signal (same contract the mobile app fulfills via its apiFetch wrapper).
 *
 * Many services call fetch() directly instead of going through config/api.ts,
 * so intercepting at the window.fetch level is the only way to guarantee
 * coverage. The header is only added for API origins — attaching a custom
 * header to third-party requests (Cognito, Firebase, Stripe) would break
 * their CORS preflight.
 */
import { getActiveOrgId } from './tenant';

const API_ORIGINS = new Set(
  [
    process.env.REACT_APP_API_URL || 'http://localhost:3000',
    'https://api.nupeloop.com',
    'https://api.konklave.app',
    'http://localhost:3000',
  ].map(url => new URL(url).origin),
);

export function installTenantFetchInterceptor(): void {
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const url =
        typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
      const origin = new URL(url, window.location.origin).origin;

      if (API_ORIGINS.has(origin)) {
        const headers = new Headers(
          init?.headers || (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has('x-organization-id')) {
          headers.set('x-organization-id', getActiveOrgId());
        }
        init = { ...init, headers };
      }
    } catch {
      // Malformed URL — let the original fetch surface the error.
    }
    return originalFetch(input, init);
  };
}
